"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";

const LOADER_CONFIG = {
  minLoadTime: 800,
  fadeOutDuration: 0.5,
  floatDuration: 2,
  pulseDuration: 1.5,
  dotStagger: 0.2,
} as const;

export default function PageLoader({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);

    const minLoadTime = setTimeout(() => {
      setIsLoading(false);
    }, LOADER_CONFIG.minLoadTime);

    return () => clearTimeout(minLoadTime);
  }, []);

  return (
    <>
      <AnimatePresence mode="wait">
        {isLoading && (
          <motion.div
            key="loader"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: LOADER_CONFIG.fadeOutDuration, ease: "easeInOut" }}
            className="fixed inset-0 z-[var(--z-top)] flex items-center justify-center bg-gradient-to-b from-[#bfe6ff] via-[#8ccaff] to-[#66aafc]"
          >
            <div className="flex flex-col items-center gap-6">
              {/* Animated Coffee Image */}
              <motion.div
                initial={{ y: 0, rotate: 0 }}
                animate={{
                  y: [-10, 10, -10],
                  rotate: [-5, 5, -5],
                }}
                transition={{
                  duration: LOADER_CONFIG.floatDuration,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative"
              >
                <div className="h-24 w-24 rounded-full border-[6px] border-[var(--dark-blue)] bg-[var(--beige)] shadow-[8px_8px_0_var(--dark-blue)] flex items-center justify-center overflow-hidden">
                  <motion.img
                    src="/img/kaffee.webp"
                    alt="Loading Coffee"
                    animate={{
                      scale: [1, 1.1, 1],
                    }}
                    transition={{
                      duration: LOADER_CONFIG.pulseDuration,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="h-16 w-16 object-contain"
                  />
                </div>
              </motion.div>

              {/* Loading Text */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="flex flex-col items-center gap-2"
              >
                <h2 className="font-luckiest text-2xl text-[#FFE066] stroke-text">
                  Wird geladen...
                </h2>
                
                {/* Animated Dots */}
                <div className="flex gap-2">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0.3 }}
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{
                        duration: LOADER_CONFIG.pulseDuration,
                        repeat: Infinity,
                        delay: i * LOADER_CONFIG.dotStagger,
                        ease: "easeInOut",
                      }}
                      className="h-3 w-3 rounded-full bg-[var(--dark-blue)] border-2 border-white"
                    />
                  ))}
                </div>
              </motion.div>

              {/* Decorative Cloud */}
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 0.6 }}
                transition={{ delay: 0.3, duration: 1, ease: "easeOut" }}
                className="absolute bottom-20 left-10 h-12 w-24 rounded-full border-[4px] border-white/85 bg-white shadow-[0px_6px_0px_rgba(0,0,0,0.1)]"
              >
                <div className="absolute -top-6 left-4 h-14 w-14 rounded-full bg-white" />
              </motion.div>

              <motion.div
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 0.6 }}
                transition={{ delay: 0.4, duration: 1, ease: "easeOut" }}
                className="absolute top-20 right-16 h-10 w-20 rounded-full border-[4px] border-white/85 bg-white shadow-[0px_6px_0px_rgba(0,0,0,0.1)]"
              >
                <div className="absolute -top-5 left-3 h-12 w-12 rounded-full bg-white" />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content with fade-in */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isHydrated && !isLoading ? 1 : 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="min-h-screen"
      >
        {children}
      </motion.div>
    </>
  );
}
